import React, { useCallback, useState } from "react";
import { ChevronRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ExamSelector from "../components/ExamSelector";
import InstructionsSection from "../components/InstructionsSection";
import PolandSection from "../components/PolandSection";
import { trackEvent } from "../lib/analytics";

const MAIN_EXAMS = [
    {
        id: "academic",
        tag: "Academic",
        name: "LANGUAGECERT Academic",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-academic/academic",
    },
    {
        id: "esol",
        tag: "International ESOL",
        name: "LANGUAGECERT ESOL",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-international-esol/b2-communicator-2281",
    },
    {
        id: "lte",
        tag: "Test of English",
        name: "LANGUAGECERT Test of English (LTE)",
        bookUrl:
            "https://www.languagecert.org/en/language-exams/english/languagecert-test-of-english-lte/lte-4skills",
    },
];

const MAIN_CODE = "217C68";

export default function LandingPage() {
    const [selectedId, setSelectedId] = useState(null);
    const selectedExam = MAIN_EXAMS.find((e) => e.id === selectedId) || null;

    const handleSelect = useCallback((id) => {
        setSelectedId(id);
        trackEvent("exam_select", { surface: "main", exam_id: id });
    }, []);

    const handleReset = useCallback(() => {
        setSelectedId(null);
    }, []);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(MAIN_CODE);
        } catch (_e) {
            const ta = document.createElement("textarea");
            ta.value = MAIN_CODE;
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
            } catch (_err) {}
            document.body.removeChild(ta);
        }
        trackEvent("code_copy", {
            surface: "main",
            exam_id: selectedId,
            code: MAIN_CODE,
        });
        const instr = document.getElementById("instructions");
        if (instr) {
            instr.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [selectedId]);

    const scrollToPoland = useCallback(() => {
        trackEvent("poland_scroll");
        const el = document.getElementById("poland-codes");
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    const lastUpdated = new Intl.DateTimeFormat("en-US", {
        month: "long",
        year: "numeric",
    }).format(new Date());

    return (
        <div id="top" data-testid="landing-page" className="min-h-screen flex flex-col bg-white">
            <Header />

            {/* HERO */}
            <section
                data-testid="hero-section"
                className="hero-bg flex-shrink-0"
            >
                <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20">
                    <div className="max-w-3xl mx-auto text-center">
                        <span
                            data-testid="hero-eyebrow"
                            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#D32F2F]"
                        >
                            <span className="inline-block w-6 h-px bg-[#D32F2F]" />
                            Booking code
                            <span className="inline-block w-6 h-px bg-[#D32F2F]" />
                        </span>
                        <h1
                            data-testid="hero-headline"
                            className="mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.1] font-semibold tracking-tight text-[#0A1128] font-[Outfit]"
                        >
                            Use this code when booking your{" "}
                            <span className="text-[#1B2A47]">
                                LanguageCert
                            </span>{" "}
                            exam
                        </h1>
                        <p
                            data-testid="hero-trust-text"
                            className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto"
                        >
                            LanguageCert is a trusted English exam chosen by
                            international candidates.
                        </p>
                        <div
                            data-testid="last-updated-stamp"
                            className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[11px] font-medium text-[#475569]"
                        >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#16A34A]" />
                            Last updated: {lastUpdated}
                        </div>
                    </div>

                    <div className="mt-14 sm:mt-16">
                        <p
                            data-testid="exam-instruction-text"
                            className="text-center text-sm text-[#64748B] mb-8"
                        >
                            Select your LanguageCert exam to view the correct
                            booking link and code
                        </p>

                        <ExamSelector
                            exams={MAIN_EXAMS}
                            selectedExam={selectedExam}
                            onSelect={handleSelect}
                            onReset={handleReset}
                            onCopy={handleCopy}
                        />

                        {!selectedExam && (
                            <div className="mt-10 flex justify-center">
                                <button
                                    type="button"
                                    onClick={scrollToPoland}
                                    data-testid="poland-strip-card"
                                    className="group inline-flex items-center gap-3 bg-white border border-[#E2E8F0] hover:border-[#1B2A47] hover:bg-[#F8F9FA] rounded-full pl-5 pr-3 py-2.5 transition-all shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1B2A47] focus-visible:ring-offset-2"
                                >
                                    <span className="inline-block w-4 h-3 overflow-hidden rounded-sm border border-[#E2E8F0]">
                                        <span className="block h-1.5 bg-white" />
                                        <span className="block h-1.5 bg-[#DC143C]" />
                                    </span>
                                    <span className="text-xs sm:text-sm font-medium text-[#1B2A47]">
                                        Booking from Poland? View Poland-only
                                        exam codes
                                    </span>
                                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F1F5F9] group-hover:bg-[#1B2A47] group-hover:text-white transition-colors">
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* INSTRUCTIONS */}
            <InstructionsSection selectedExam={selectedExam} />

            {/* POLAND */}
            <PolandSection />

            <Footer />
        </div>
    );
}
